import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Loader } from '@huggg/react-components';
import { Huggg } from 'components';
import SegmentRedemption from './Segments/SegmentRedemption';
import styles from './voucher.module.scss';
import { PrepaidLoader } from 'components/ui/PrepaidCard/PrepaidLoader';
import {
  isTilloVoucher,
  tilloVoucherError,
  voucherRedemptionError
} from 'utilities/errorHandling';

/**
 * Display the QR Code voucher to the user should they wish to redeem their
 * huggg. Will show segments if any are available.
 *
 * @param {Object} props
 */
const Voucher = props => {
  const history = useHistory();

  const {
    hasVoucherToClaim,
    huggg,
    isGenerating,
    isTestHugggRedeemed,
    onSegmentConfirm,
    selectedSegment,
    vouchers,
    voucherError,
    hideProductImage,
    isFormRedemptionType,
    isPin4Type,
    isPrepaidCardType,
    isBankPaymentType,
    defaultTerritory
  } = props;

  const hasVouchers = Boolean(vouchers && vouchers.length);
  const hasFulFilVoucher =
    hasVouchers && vouchers.some(voucher => voucher.type === 'fulfil');
  // If the voucher requires no specific redemption flow, just call claim straight away
  useEffect(() => {
    if (
      (!hasVouchers || hasVoucherToClaim) &&
      selectedSegment &&
      selectedSegment.id &&
      !isFormRedemptionType &&
      !isPin4Type &&
      !isPrepaidCardType &&
      !isBankPaymentType &&
      !hasFulFilVoucher
    ) {
      console.log('Voucher: onSegmentConfirm', {
        selectedSegment
      });
      onSegmentConfirm(selectedSegment, { territory: defaultTerritory });
    }
  }, [
    hasVouchers,
    hasVoucherToClaim,
    selectedSegment,
    isFormRedemptionType,
    isPin4Type,
    isPrepaidCardType,
    isBankPaymentType,
    defaultTerritory,
    hasFulFilVoucher,
    onSegmentConfirm
  ]);

  const shouldShowHuggg = !['form-redemption', 'prepaid-card'].includes(
    selectedSegment?.voucherType
  );

  const getLoader = useCallback(() => {
    return selectedSegment?.voucherType === 'prepaid-card' ? (
      <PrepaidLoader />
    ) : (
      <Loader />
    );
  }, [selectedSegment]);

  const isTillo = isTilloVoucher(selectedSegment?.voucherType);

  const getError = useCallback(() => {
    if (selectedSegment?.voucherType === 'prepaid-card') {
      return null;
    }

    const errorContent = isTillo
      ? tilloVoucherError(huggg.brand_name)
      : voucherRedemptionError();

    return <div className={styles.error}>{errorContent}</div>;
  }, [selectedSegment, isTillo, huggg.brand_name]);

  if (isTestHugggRedeemed) {
    history.push(`/test/${huggg.shortcode}/test-redeemed`);
  }

  return (
    <div data-test-id="voucher-segment">
      {shouldShowHuggg && (
        <div style={{ marginBottom: 20 }}>
          <Huggg huggg={huggg} hideImage={hideProductImage} />
        </div>
      )}

      {voucherError && getError()}

      {huggg.id && (
        <SegmentRedemption
          huggg={huggg}
          selectedSegment={selectedSegment}
          onConfirm={onSegmentConfirm}
          data-test-id={
            hasVouchers
              ? 'voucher-claimed'
              : 'voucher-segment-redemption-component'
          }
          isFormRedemptionType={isFormRedemptionType}
          isPin4Type={isPin4Type}
          isPrepaidCardType={isPrepaidCardType}
          isBankPaymentType={isBankPaymentType}
          defaultTerritory={defaultTerritory}
          shouldShowRetry={
            Boolean(voucherError) && !isGenerating && !isTillo
          }
        />
      )}

      {isGenerating && getLoader()}
    </div>
  );
};

Voucher.propTypes = {
  onSegmentConfirm: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isTestHugggRedeemed: PropTypes.bool.isRequired,
  selectedSegment: PropTypes.object,
  brandLocation: PropTypes.string,
  error: PropTypes.object,
  expiryDate: PropTypes.instanceOf(Date),
  vouchers: PropTypes.array,
  voucherError: PropTypes.object,
  hideProductImage: PropTypes.bool,
  isFormRedemptionType: PropTypes.bool,
  isPin4Type: PropTypes.bool,
  isPrepaidCardType: PropTypes.bool,
  isBankPaymentType: PropTypes.bool,
  defaultTerritory: PropTypes.string,
  onFulfilClick: PropTypes.func,
  hasVoucherToClaim: PropTypes.bool.isRequired
};

export default Voucher;
